import { Client } from '@langchain/langgraph-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

const LANGGRAPH_API_URL =
  'http://internal-im-agents-lb-dev-1743894815.us-east-1.elb.amazonaws.com:8123';
const GRAPH_ID = 'marketing_chatbot';

// Time in ms to wait before showing "thinking" indicator when no chunks arrive
const THINKING_TIMEOUT_MS = 1500;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export function useMusicIqChatbot() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  const clientRef = useRef<Client | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Make sure to clean up the timer on unmount.
  useEffect(() => {
    clearTimeout(thinkingTimeoutRef.current);
  }, []);

  // ----------------------------------------------------------------------------
  //
  // !!!!! NOTE !!!!!
  //
  // The back end implementation with LangGraph is going away and will be replaced
  // by AWS Bedrock. So the implementation in this hook will have to change once
  // the back end is updated. For now, to develop the front end, we can still use
  // the LangGraph implementation.
  //
  // ----------------------------------------------------------------------------

  // Initialize client
  useEffect(() => {
    clientRef.current = new Client({ apiUrl: LANGGRAPH_API_URL });
  }, []);

  // Create thread on mount
  useEffect(() => {
    async function createThread() {
      if (!clientRef.current) {
        return;
      }

      try {
        const thread = await clientRef.current.threads.create();

        setThreadId(thread.thread_id);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to create thread'),
        );
      }
    }

    createThread();
  }, []);

  const abort = useCallback(() => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = undefined;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsThinking(false);
    }
  }, []);

  const ask = useCallback(
    async (content: string) => {
      if (!clientRef.current || !threadId) {
        setError(new Error('Chat not initialized'));

        return;
      }

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      // Add user message immediately
      setMessages((prev) => [
        ...prev,
        { id: userMessageId, role: 'user', content },
      ]);

      setIsThinking(true);
      setError(null);

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      const { signal } = abortControllerRef.current;

      try {
        // Add empty assistant message that will be updated with streamed content
        setMessages((prev) => [
          ...prev,
          { id: assistantMessageId, role: 'assistant', content: '' },
        ]);

        const streamResponse = clientRef.current.runs.stream(
          threadId,
          GRAPH_ID,
          {
            input: { messages: [{ role: 'human', content }] },
            streamMode: 'messages-tuple',
            signal,
          },
        );

        let accumulatedContent = '';

        for await (const chunk of streamResponse) {
          if (chunk.event === 'messages' && Array.isArray(chunk.data)) {
            const [message] = chunk.data;
            // @TODO WMG1-908 - The casting will be refactored when we switch to AWS Bedrock.
            const textPiece = (message?.content?.[0] as { text: string })?.text;

            if (
              // @TODO WMG1-908 - Ask back end why not 'ai' (according to langgraph).
              // But might not be relevant when using AWS Bedrock.
              // message?.type === 'AIMessageChunk' &&
              typeof textPiece === 'string'
            ) {
              // Content received, no longer "thinking"
              setIsThinking(false);

              // Reset the thinking timeout - if no chunk arrives within the
              // threshold, we'll show the thinking indicator again
              if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
              }

              thinkingTimeoutRef.current = setTimeout(() => {
                setIsThinking(true);
              }, THINKING_TIMEOUT_MS);

              accumulatedContent += textPiece;

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: accumulatedContent }
                    : msg,
                ),
              );
            }
          }
        }

        // @TODO WMG1-908 - Remove after testing...
        // console.log('accumulatedContent:', accumulatedContent);
      } catch (err) {
        // Don't show error if the request was aborted by the user
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        setError(
          err instanceof Error ? err : new Error('Failed to send message'),
        );

        // Remove the empty assistant message on error
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== assistantMessageId),
        );
      } finally {
        if (thinkingTimeoutRef.current) {
          clearTimeout(thinkingTimeoutRef.current);
          thinkingTimeoutRef.current = undefined;
        }

        abortControllerRef.current = null;
        setIsThinking(false);
      }
    },
    [threadId],
  );

  return { messages, ask, abort, isThinking, error };
}
